class PredictiveSearch extends withAnimation(SearchForm) {
  constructor() {
    super();

    this.cachedResults = {};
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isInitialOpen = true;
    this.scrollLockEnable = true;
    this.closeByKeyboard = true;
    this.requestSequence = 0;
    this.returnFocusElement = null;

    this.initialized = false;

    this.scheduleIdleInit();
  }

  scheduleIdleInit() {
    const init = () => this.initComponent();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(init, { timeout: 2000 });
    } else {
      setTimeout(init, 0);
    }
  }

  initComponent() {
    if (this.initialized) return;
    this.initialized = true;

    this.searchTemplate = this.closest('.template-search__search');
    this.predictiveSearchWrapper = this.querySelector('[data-predictive-search-wrapper]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.predictiveSearchToggle = this.querySelector('[data-predictive-search-toggle]');
    this.predictiveSearchClose = this.querySelectorAll('[data-predictive-search-close]');
    this.scrollTrack = this.querySelector('custom-scrollbar');
    this.backDrop = this.querySelector('.modal__backdrop');
    this.modalTop = this.querySelector('.predictive-search__modal-top');
    this.customCursor = this.querySelector('custom-cursor');

    this.enhanceSearchSurface();

    DrawerManager.register(this);

    this.isFull = this.predictiveSearchToggle ? true : false;

    this.initAnimation({
      duration: 0.3,
      staggerDuration: this.isFull ? 0.35 : 0.2,
      stageDelay: this.isFull ? 0.1 : 0.05,
      openedPosition: this.isFull ? 'fixed' : 'absolute'
    });

    this.setupEventListeners();
  }

  get isDrawerMode() {
    return window.DeviceUtils.isMobile && !this.searchTemplate;
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('click', this.onInputClick.bind(this));
    this.input.addEventListener('focusout', this.onInputFocusOut.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.isFull && this.predictiveSearchToggle.addEventListener('click', this.onToggleClick.bind(this));
    this.backDrop?.addEventListener('click', this.onClickClose.bind(this));
    this.predictiveSearchClose.forEach((button) => {
      if (button === this.backDrop) return;
      button.addEventListener('click', this.onClickClose.bind(this));
      button.addEventListener('keyup', (event) => {
        if (event.code === 'Enter' || event.code === 'Space') {
          event.preventDefault();
          event.stopPropagation();
          this.onClickClose();
        }
      });
    });
  }

  enhanceSearchSurface() {
    const isArabic = document.documentElement.lang?.toLowerCase().startsWith('ar');
    const inputId = this.input.id || `Predictive-Search-${Math.random().toString(36).slice(2, 9)}`;

    if (!this.input.id) this.input.id = inputId;

    if (this.predictiveSearchToggle) {
      const searchPrompt = isArabic
        ? 'رقم القطعة، OEM أو اسم السيارة'
        : 'Part number, OEM or vehicle';
      const inputLabel = this.input.form?.querySelector(`label[for="${CSS.escape(inputId)}"]`);

      this.input.placeholder = searchPrompt;
      this.input.setAttribute('aria-label', searchPrompt);
      if (inputLabel) inputLabel.textContent = searchPrompt;
    }

    if (this.predictiveSearchToggle && this.predictiveSearchWrapper) {
      if (!this.predictiveSearchWrapper.id) {
        this.predictiveSearchWrapper.id = `${inputId}-Overlay`;
      }

      this.predictiveSearchToggle.setAttribute('role', 'button');
      this.predictiveSearchToggle.setAttribute('aria-label', isArabic ? 'فتح البحث' : 'Open search');
      this.predictiveSearchToggle.setAttribute('aria-controls', this.predictiveSearchWrapper.id);
      this.predictiveSearchToggle.setAttribute('aria-expanded', 'false');

      this.predictiveSearchWrapper.setAttribute('role', 'dialog');
      this.predictiveSearchWrapper.setAttribute('aria-modal', 'true');
      this.predictiveSearchWrapper.setAttribute('aria-label', isArabic ? 'البحث في المتجر' : 'Search the store');
      this.predictiveSearchWrapper.setAttribute('aria-hidden', 'true');
    }

    if (this.modalTop) {
      let heading = this.modalTop.querySelector('[data-sf-search-heading]');
      if (!heading) {
        heading = document.createElement('div');
        heading.className = 'sf-search-overlay__heading';
        heading.setAttribute('data-sf-search-heading', '');
        heading.innerHTML = isArabic
          ? '<strong>ابحث في سنفينيكو</strong><span>منتجات وقطع غيار برقم القطعة أو السيارة</span>'
          : '<strong>Search SENFINECO</strong><span>Products and parts by number or vehicle</span>';
        this.modalTop.prepend(heading);
      }

      let closeButton = this.modalTop.querySelector(
        '[data-predictive-search-close], [data-sfv2-search-close], [data-sf-search-close]'
      );
      if (!closeButton) {
        closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'sf-search-overlay__close sfv2-search-close';
        closeButton.setAttribute('data-sf-search-close', '');
        closeButton.setAttribute('data-sfv2-search-close', '');
        closeButton.setAttribute('data-predictive-search-close', '');
        closeButton.setAttribute('aria-label', isArabic ? 'إغلاق البحث' : 'Close search');
        closeButton.innerHTML = '<span aria-hidden="true">&times;</span>';
        this.modalTop.appendChild(closeButton);
      }
    }

    this.predictiveSearchClose = this.querySelectorAll(
      '[data-predictive-search-close], [data-sfv2-search-close], [data-sf-search-close]'
    );
    this.showEmptyState();
  }

  showEmptyState() {
    if (!this.predictiveSearchResults || this.getQuery().length) return;

    const isArabic = document.documentElement.lang?.toLowerCase().startsWith('ar');
    this.predictiveSearchResults.innerHTML = `
      <div class="sf-search-overlay__empty" data-sf-search-empty role="status">
        <span class="sf-search-overlay__empty-icon" aria-hidden="true">⌕</span>
        <strong>${isArabic ? 'ماذا تبحث عنه؟' : 'What are you looking for?'}</strong>
        <span>${
          isArabic
            ? 'اكتب رقم القطعة أو OEM أو اسم السيارة.'
            : 'Enter a part number, OEM number or vehicle.'
        }</span>
      </div>`;
  }

  getQuery() {
    return this.input.value.trim();
  }

  onToggleClick(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.returnFocusElement = this.predictiveSearchToggle;
    this.isOpen ? this.close(false, true) : this.onFocus();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (this.searchTemplate) {
      if (newSearchTerm.length == 0) {
        this.close(false, true);
        this.renderSearchResults('');
      } else {
        this.open();
      }
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.removeAttribute('results');
      this.showEmptyState();
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const submitter = event.submitter || document.activeElement;
    const viewAllButton = submitter?.closest?.('.predictive-search__search-for-button');

    // The header search button and Enter key update this overlay. Only the
    // explicit "View all" action submits the form to the full search page.
    if (viewAllButton && this.getQuery().length) return;

    event.preventDefault();
    const query = this.getQuery();
    if (!query.length) {
      this.showEmptyState();
      this.open();
      this.input.focus({ preventScroll: true });
      return;
    }

    const shouldFetch = this.searchTerm !== query || this.getAttribute('results') !== 'true';
    this.searchTerm = query;
    this.open();
    if (shouldFetch) this.getSearchResults(query);
    this.input.focus({ preventScroll: true });
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      if (this.searchTemplate) {
        this.renderSearchResults('');
      }
      this.showEmptyState();
    }
  }

  onInputClick() {
    this.isDrawerMode && document.body.classList.add('has-menu-disabled');
  }

  onInputFocusOut() {
    this.isDrawerMode && document.body.classList.remove('has-menu-disabled');
  }

  onFocus() {
    if (!this.initialized) {
      this.initComponent();
    }

    const currentSearchTerm = this.getQuery();
    this.updateMaxHeight();

    if (this.searchTemplate && currentSearchTerm.length && !this.isOpen) {
      if (this.searchTerm !== currentSearchTerm) {
        this.searchTerm = currentSearchTerm;
        this.getSearchResults(currentSearchTerm);
      } else if (this.getAttribute('results') !== 'true') {
        this.getSearchResults(this.searchTerm);
      } else {
        this.open();
      }
      return;
    }

    this.open();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') !== 'true') {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    if (this.isDrawerMode || !this.closeByKeyboard) return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close(false, this.isFull);
    });
  }

  onKeyup(event) {
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        if (this.isFull && !this.isOpen) {
          this.onFocus();
          return;
        }
        this.selectOption();
        break;
      case 'Escape':
        if (this.isFull && this.isOpen) {
          this.input.blur();
          setTimeout(() => {
            this.close(false, true);
            if (this.predictiveSearchToggle) {
              this.predictiveSearchToggle.focus();
            }
          }, 0);
          event.stopPropagation();
          return;
        }
        if (!this.isOpen) return;
        this.input.blur();
        setTimeout(() => {
          this.close(false, true);
        }, 0);
        event.stopPropagation();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    if (this.isFull && this.isOpen && event.code === 'Tab') {
      this.trapTab(event);
    }
  }

  trapTab(event) {
    const container = this.predictiveSearchWrapper;
    if (!container) return;

    const focusable = getFocusableElements(container).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !container.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !container.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  onClickClose(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.close(false, true);
    this.closeByKeyboard = false;
    setTimeout(() => {
      this.closeByKeyboard = true;
      const focusTarget = this.returnFocusElement || this.predictiveSearchToggle;
      focusTarget?.focus?.({ preventScroll: true });
    }, 500);
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'gi')) || [];
      if (matches.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(new RegExp(previousTerm, 'i'), newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li a, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i < allVisibleElements.length) {

      if (allVisibleElements[i] === selectedElement || allVisibleElements[i].parentElement === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    const parentElement = activeElement.parentElement;

    parentElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', parentElement.id || activeElement.id);

    activeElement.focus();
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.trim().replace(/\s+/g, '-').toLowerCase();
    const requestSequence = ++this.requestSequence;

    this.abortController.abort();
    this.abortController = new AbortController();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.setLiveRegionResults();
      return;
    }
    const isArabicLocale = document.documentElement.lang?.toLowerCase().startsWith('ar');
    const predictiveSearchUrl = isArabicLocale ? '/en/search/suggest' : routes.predictive_search_url;
    const searchableProductFields = [
      'title',
      'product_type',
      'variants.title',
      'vendor',
      'variants.sku',
      'variants.barcode',
      'tag',
    ].join(',');
    const searchParameters = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[options][fields]': searchableProductFields,
    });
    fetch(`${predictiveSearchUrl}?${searchParameters.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          return '';
        }

        return response.text();
      })
      .then((text) => {
        if (requestSequence !== this.requestSequence || this.getQuery() !== searchTerm) return;

        if (!text) {
          this.removeAttribute('loading');
          this.removeAttribute('results');
          return;
        }

        const parsedResultsSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!parsedResultsSection) {
          this.removeAttribute('loading');
          this.removeAttribute('results');
          return;
        }

        if (isArabicLocale) {
          parsedResultsSection.querySelectorAll('a[href^="/en/"]').forEach((link) => {
            link.setAttribute('href', link.getAttribute('href').replace(/^\/en(?=\/)/, ''));
          });
          const arabicLabels = {
            'predictive-search-queries': 'اقتراحات',
            'predictive-search-products': 'المنتجات',
            'predictive-search-collections': 'الأقسام',
            'predictive-search-pages-mobile': 'الصفحات',
          };
          Object.entries(arabicLabels).forEach(([id, label]) => {
            const heading = parsedResultsSection.querySelector(`#${id}`);
            if (heading) heading.textContent = label;
          });
          parsedResultsSection.querySelectorAll('.button--arrow span').forEach((label) => {
            label.textContent = 'عرض الكل';
          });
          const textWalker = document.createTreeWalker(parsedResultsSection, NodeFilter.SHOW_TEXT);
          let textNode;
          while ((textNode = textWalker.nextNode())) {
            textNode.nodeValue = textNode.nodeValue
              .replace(/Vendor:/gi, 'العلامة:')
              .replace(/Regular price/gi, 'السعر العادي')
              .replace(/Sale price/gi, 'سعر العرض')
              .replace(/Unit price/gi, 'سعر الوحدة')
              .replace(/(\d+)\s+items?\b/gi, '$1 منتج')
              .replace(/\bper\b/gi, 'لكل');
          }
        }

        const resultsMarkup = parsedResultsSection.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        this.setLiveRegionResults();
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.removeAttribute('loading');
        this.removeAttribute('results');
        console.warn('Predictive search request failed', error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  scrollTop() {
    const predictiveSearchScroll = this.querySelectorAll('.predictive-search-scroll-wrapper, .predictive-search-results__scroll');
    predictiveSearchScroll.forEach((scroll) => {
      scroll.scrollTop = 0;
    });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.enhanceRenderedResults();
    this.setAttribute('results', true);
    this.scrollTop();
    this.animateSearchResultsStagger();

    if (this.searchTemplate && !this.isOpen) {
      this.open();
    }
  }

  enhanceRenderedResults() {
    const groupsWrapper = this.predictiveSearchResults.querySelector('.predictive-search__results-groups-wrapper');
    const productsGroup = groupsWrapper?.querySelector('.predictive-search__products');
    if (groupsWrapper && productsGroup && groupsWrapper.firstElementChild !== productsGroup) {
      groupsWrapper.prepend(productsGroup);
    }

    const viewAllButton = this.predictiveSearchResults.querySelector(
      '.predictive-search__search-for-button button'
    );
    if (viewAllButton) {
      const isArabic = document.documentElement.lang?.toLowerCase().startsWith('ar');
      viewAllButton.type = 'submit';
      viewAllButton.setAttribute('data-sf-search-view-all', '');
      viewAllButton.setAttribute('aria-label', isArabic ? 'عرض كل نتائج البحث' : 'View all search results');
    }
  }

  animateSearchResultsStagger() {
    const staggerSelector = '.predictive-search__result-group';

    const staggerItems = this.predictiveSearchResults.querySelectorAll(staggerSelector);
    if (!staggerItems.length) {
      return;
    }

    this.animateStagger(this.predictiveSearchResults, {
      staggerSelector: staggerSelector,
      animationType: 'fade',
      slideDirection: 'right',
      delay: 0
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countValue = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(countValue?.textContent || '');
  }

  getResultsMaxHeight() {
    if (this.isFull) return null;

    const bottomPosition = this.searchTemplate
      ? this.searchTemplate.getBoundingClientRect().bottom
      : (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);

    const offset = this.searchTemplate ? 20 : 30;

    return Math.round(window.innerHeight - bottomPosition - offset);
  }

  updateMaxHeight() {
    if (this.isFull) return;
    const maxHeight = this.getResultsMaxHeight();
    document.documentElement.style.setProperty('--predictive-search-max-height', `${maxHeight}px`);
  }

  open() {
    if (this.isOpen) return;
    this.customCursor?.disable?.();;

    const megaMenu = document.querySelector('mega-menu');
    if (megaMenu?.classList.contains('mega-menu--block')) {
      megaMenu.classList.remove('mega-menu--block');
      document.body.removeAttribute('data-mega-menu-block');
      megaMenu.forceClose?.();
    }

    const isDrawerMode = this.isDrawerMode;

    if (isDrawerMode) {
      this.scrollLockEnable && scrollLock.lock();
      DeviceUtils.updateHeaderSizes();
      DrawerManager.setActiveWithSecondary(this);
      if (DrawerManager.diffrentSide()) {
        DrawerManager.clearSecondaryIfOpen(this);
      }
      document.body.classList.add('overflow-hidden');
    }
    this.updateMaxHeight();
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.predictiveSearchToggle?.setAttribute('aria-expanded', 'true');
    this.predictiveSearchWrapper?.setAttribute('aria-hidden', 'false');
    this.isOpen = true;

    if (this.isFull) {
      requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
    }

    const header = document.querySelector('.section-header');
    if (header && !header.classList.contains('shopify-section-header-sticky')) {
      const top = Math.round(header.getBoundingClientRect().top);
      this.style.setProperty('--predictive-search-header-offset', `${top}px`);
    }

    if (this.isInitialOpen) {
      requestAnimationFrame(() => {
        if (this.isFull && !isDrawerMode) {
          document.body.classList.add('overflow-hidden');
          const modalTopHeight = this.modalTop?.getBoundingClientRect().height || 0;
          document.documentElement.style.setProperty('--predictive-search-top-max-height', `${modalTopHeight}px`);
        }
        this.animateShowContent(isDrawerMode);
      });
      this.isInitialOpen = false;
    }
  }

  close(clearSearchTerm = false, animateEnable = false) {
    if (this._isClosing) return;
    this._isClosing = true;

    this.isOpen = false;
    this.isInitialOpen = true;
    this.closeResults(clearSearchTerm, animateEnable);
  }

  closeResults(clearSearchTerm = false, animateEnable = false) {
    this.scrollTop();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.removeAttribute('loading');

    const isDrawerMode = this.isDrawerMode;

    if (this.scrollLockEnable && animateEnable) {
      if (isDrawerMode || !this.input.matches(':focus')) {
        requestAnimationFrame(() => this.animateHideContent(isDrawerMode));
      }
    } else {
      this.onCloseActions(false)
    }
  }

  onCloseActions() {
    this._isClosing = false;
    if (this.isOpen) return;
    document.body.classList.remove('predictive-search-open');
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchToggle?.setAttribute('aria-expanded', 'false');
    this.predictiveSearchWrapper?.setAttribute('aria-hidden', 'true');
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.customCursor?.disable?.();
    this.style.removeProperty('--predictive-search-header-offset');

    if (this.isDrawerMode) {
      DrawerManager.unsetActive(this);
      document.body.classList.remove('has-menu-disabled');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  onShowActions(isDrawerMode) {
    document.body.classList.add('predictive-search-open');
    if (this.isFull) {
      requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
    }
    this.scrollTrack?.updateThumbPosition?.();
    if (isDrawerMode) {
      DrawerManager.clearSecondaryIfOpen(this);
    }
    this.customCursor?.enable?.();

  }

  forceClose() {
    if (!this.isOpen) return;
    this.close();
    this.input.blur();
    if (this.backDrop) this.backDrop.style.opacity = '0';
  }

  async animateShowContent(isDrawerMode) {
    const method = isDrawerMode ? 'animateSlideIn' : this.isFull ? 'animateSlideIn' : 'animateShow';
    const options = {
      slideDirection: 'top',
      animationType: 'fade',
      backDrop: isDrawerMode ? this.backDrop : this.isFull ? this.backDrop : false,
      onComplete: () => this.onShowActions(isDrawerMode),
      ...(!isDrawerMode && {
        staggerSelector: '.predictive-search__result-group',
        maxHeight: this.getResultsMaxHeight()
      })
    };

    await this[method](this.predictiveSearchWrapper, options);
  }

  async animateHideContent(isDrawerMode) {
    const method = isDrawerMode ? 'animateSlideOut' : this.isFull ? 'animateSlideOut' : 'animateHide';
    const options = {
      slideDirection: 'top',
      backDrop: isDrawerMode ? this.backDrop : this.isFull ? this.backDrop : false,
      onComplete: () => this.onCloseActions()
    };

    await this[method](this.predictiveSearchWrapper, options);
  }
}

customElements.define('predictive-search', PredictiveSearch);
